/**
 * Custom element that toggles the announcement bar between
 * the default marquee and a free-shipping progress bar.
 *
 * Reacts to the theme-wide `cart:update` event. Every cart mutator in this
 * theme already dispatches it, so we don't need to patch window.fetch —
 * doing both produced a duplicate /cart.js per mutation which queued on
 * Shopify's cart connection and serialized subsequent /cart/add.js calls.
 */
class AnnouncementShippingBar extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.successText = this.dataset.successText || '';
    this.underText = this.dataset.underText || '';

    // Optional reward tiers: [{ threshold: <cents>, label: <string> }, ...]
    this.tiers = [];
    try {
      const parsed = JSON.parse(this.dataset.tiers || '[]');
      if (Array.isArray(parsed)) {
        this.tiers = parsed
          .filter((t) => t && Number(t.threshold) > 0)
          .sort((a, b) => a.threshold - b.threshold);
      }
    } catch (e) {
      this.tiers = [];
    }
    this.marqueeEl = this.querySelector('[data-announcement-marquee]');
    this.shippingEl = this.querySelector('[data-announcement-shipping]');
    this.messageEl = this.querySelector('[data-shipping-message]');
    this.meterEl = this.querySelector('[data-shipping-meter]');
    this.dotEls = Array.from(this.querySelectorAll('[data-shipping-dot]'));

    if (!this.threshold || !this.marqueeEl || !this.shippingEl) return;

    // Check initial cart state
    this.#fetchCartAndUpdate(false);

    // Listen for theme cart events
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#onCartUpdate);
  }

  #onCartUpdate = () => {
    this.#fetchCartAndUpdate(true);
  };

  #fetchPending = false;

  async #fetchCartAndUpdate(animate) {
    // Debounce: skip if a fetch is already in-flight
    if (this.#fetchPending) return;
    this.#fetchPending = true;

    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await res.json();
      this.#update(cart, animate);
    } catch (e) {
      // On error, keep current state
    } finally {
      this.#fetchPending = false;
    }
  }

  #update(cart, animate) {
    const hasItems = cart.item_count > 0;

    if (hasItems) {
      this.#showShipping(cart, animate);
    } else {
      this.#showMarquee(animate);
    }
  }

  #showShipping(cart, animate) {
    // With tiers, progress runs toward the final (highest) tier; otherwise the single threshold.
    const maxThreshold = this.tiers.length ? this.tiers[this.tiers.length - 1].threshold : this.threshold;
    const percentage = Math.min((cart.total_price / maxThreshold) * 100, 100);
    const isSuccess = cart.total_price >= maxThreshold;

    // Update message
    if (this.messageEl) {
      this.messageEl.textContent = this.#buildMessage(cart);
    }

    // Update meter
    if (this.meterEl) {
      this.meterEl.style.width = percentage + '%';
    }

    // Update tier dots: mark each reached when its threshold is met
    if (this.dotEls.length && this.tiers.length) {
      this.dotEls.forEach((dot) => {
        const tier = this.tiers[Number(dot.dataset.shippingDot)];
        dot.classList.toggle('is-reached', Boolean(tier) && cart.total_price >= tier.threshold);
      });
    }

    // Toggle success class for color change
    if (this.shippingEl) {
      this.shippingEl.classList.toggle('announcement-shipping--success', isSuccess);
    }

    // Swap visibility
    if (animate) {
      this.marqueeEl.classList.add('announcement-state--hidden');
      this.shippingEl.classList.remove('announcement-state--hidden');
      this.shippingEl.classList.add('announcement-state--visible');
    } else {
      this.marqueeEl.style.display = 'none';
      this.marqueeEl.classList.add('announcement-state--hidden');
      this.shippingEl.classList.remove('announcement-state--hidden');
      this.shippingEl.classList.add('announcement-state--visible');
      this.shippingEl.style.display = '';
    }
  }

  #showMarquee(animate) {
    if (animate) {
      this.shippingEl.classList.add('announcement-state--hidden');
      this.shippingEl.classList.remove('announcement-state--visible');
      this.marqueeEl.classList.remove('announcement-state--hidden');
      this.marqueeEl.style.display = '';
    } else {
      this.marqueeEl.style.display = '';
      this.marqueeEl.classList.remove('announcement-state--hidden');
      this.shippingEl.classList.add('announcement-state--hidden');
      this.shippingEl.classList.remove('announcement-state--visible');
    }
  }

  /**
   * Builds the shipping-bar message. Uses reward tiers when present, otherwise
   * falls back to the single free-shipping threshold text.
   *
   * @param {{ total_price: number, currency: string }} cart
   * @returns {string}
   */
  #buildMessage(cart) {
    if (!this.tiers.length) {
      if (cart.total_price >= this.threshold) return this.successText;
      const remaining = this.threshold - cart.total_price;
      return this.underText.replace('[amount]', this.#formatMoney(remaining, cart.currency));
    }

    const next = this.tiers.find((tier) => cart.total_price < tier.threshold);
    if (!next) {
      return `🎉 ${this.tiers.map((tier) => tier.label).join(', ')} unlocked!`;
    }
    const remaining = next.threshold - cart.total_price;
    return `Add ${this.#formatMoney(remaining, cart.currency)} more to unlock ${next.label}!`;
  }

  #formatMoney(cents, currencyCode) {
    const value = cents / 100;
    const currency = currencyCode || 'USD';
    // Keep whole amounts clean ($19, not $19.00); show cents otherwise.
    const hasFraction = cents % 100 !== 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (e) {
      // Fallback if the currency code is missing/unknown
      return '$' + value.toFixed(2).replace(/\.00$/, '');
    }
  }
}

if (!customElements.get('announcement-shipping-bar')) {
  customElements.define('announcement-shipping-bar', AnnouncementShippingBar);
}
